import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Button, VStack, HStack, FormControl, FormLabel, Input, InputGroup, InputRightAddon,
  Text, Radio, RadioGroup, Alert, AlertIcon, Flex, Icon
} from '@chakra-ui/react';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  computeTrajectory,
  generateJShape,
  generateSShape,
  parseCSV,
  classifyDDI
} from '../../utils/ddiTrajectoryCalculator';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const CalculatorIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M7 2h10a2 2 0 012 2v16a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2zm0 2v4h10V4H7zm0 6v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2zm-8 4v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v6h2v-6h-2zm-8 4v2h2v-2H7zm4 0v2h2v-2h-2z"
    />
  </Icon>
);

const J_DEFAULTS = { kop: '700', build: '2', inc: '30', td: '1500' };
const S_DEFAULTS = { kop1: '700', br1: '2', inc1: '30', kop2: '1500', br2: '2', inc2: '60', td: '3000' };

const ABBREVIATIONS = [
  ['KOP', 'Kick Off Point'], ['TD', 'Total Depth'], ['MD', 'Measured Depth'],
  ['INC', 'Inclination'], ['AZ', 'Azimuth'], ['AHD', 'Along Hole Displacement'],
  ['Tort', 'Tortuosity'], ['TVD', 'True Vertical Depth'], ['DDI', 'Drilling Difficulty Index'],
];

const NumInput = ({ label, value, onChange, unit, min, max, isDisabled }) => (
  <FormControl>
    <FormLabel fontSize="xs" mb={0}>{label}</FormLabel>
    <InputGroup size="sm">
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        isDisabled={isDisabled}
      />
      {unit && <InputRightAddon fontSize="xs">{unit}</InputRightAddon>}
    </InputGroup>
  </FormControl>
);

export const DDITrajectoryModal = ({ isOpen, onClose, onApply, units }) => {
  const [trajType, setTrajType] = useState('jshape');
  const [jInputs, setJInputs] = useState(J_DEFAULTS);
  const [sInputs, setSInputs] = useState(S_DEFAULTS);
  const [csvData, setCsvData] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const isMetric = units === 'metric';
  const lengthUnit = isMetric ? 'm' : 'ft';
  const rateUnit = isMetric ? '/30m' : '/100ft';

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setTrajType('jshape');
      setJInputs(J_DEFAULTS);
      setSInputs(S_DEFAULTS);
      setCsvData(null);
      setCsvFileName('');
      setResult(null);
      setError('');
    }
  }, [isOpen]);

  // Auto-recalculate on any change
  useEffect(() => {
    if (!isOpen) return;
    const useImperial = !isMetric;

    try {
      let survey;
      if (trajType === 'jshape') {
        const kop = parseFloat(jInputs.kop) || 0;
        const br = parseFloat(jInputs.build) || 0;
        const inc = parseFloat(jInputs.inc) || 0;
        const td = parseFloat(jInputs.td) || 0;
        if (!kop || !br || !inc || !td) { setResult(null); setError(''); return; }
        const conv = useImperial ? 3.28084 : 1;
        survey = generateJShape(kop / conv, br, inc, td / conv, useImperial);
      } else if (trajType === 'sshape') {
        const kop1 = parseFloat(sInputs.kop1) || 0;
        const br1 = parseFloat(sInputs.br1) || 0;
        const inc1 = parseFloat(sInputs.inc1) || 0;
        const kop2 = parseFloat(sInputs.kop2) || 0;
        const br2 = parseFloat(sInputs.br2) || 0;
        const inc2 = parseFloat(sInputs.inc2) || 0;
        const td = parseFloat(sInputs.td) || 0;
        if (!kop1 || !br1 || !inc1 || !kop2 || !br2 || !td) { setResult(null); setError(''); return; }
        const conv = useImperial ? 3.28084 : 1;
        survey = generateSShape(kop1 / conv, br1, inc1, kop2 / conv, br2, inc2, td / conv, useImperial);
      } else if (trajType === 'custom') {
        if (!csvData) { setResult(null); setError(''); return; }
        survey = csvData;
      }

      const res = computeTrajectory(survey, useImperial);
      setResult(res);
      setError('');
    } catch (err) {
      setResult(null);
      setError(err.message);
    }
  }, [isOpen, trajType, jInputs, sInputs, csvData, isMetric]);

  const handleCsvFile = useCallback((file) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a .csv file (MD, INC, AZ)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCSV(e.target.result);
        if (parsed.length < 2) {
          setError('CSV must contain at least 2 survey points');
          return;
        }
        setCsvData(parsed);
        setCsvFileName(file.name);
        setError('');
      } catch (err) {
        setError('Failed to parse CSV: ' + err.message);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleCsvFile(e.dataTransfer.files[0]);
  }, [handleCsvFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSendResult = () => {
    if (result) {
      const classification = classifyDDI(result.maxDDI);
      onApply(classification.key, result.maxDDI);
      onClose();
    }
  };

  const updateJ = (field, value) => setJInputs((prev) => ({ ...prev, [field]: value }));
  const updateS = (field, value) => setSInputs((prev) => ({ ...prev, [field]: value }));

  // Chart data
  const chartData = result ? {
    datasets: [{
      label: 'Trajectory',
      data: result.output.map((p) => ({ x: p.ahd, y: p.tvd })),
      borderColor: '#0055cc',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 0,
      fill: false,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: { display: true, text: `AHD (${lengthUnit})` },
        grid: { color: '#e2e8f0' },
      },
      y: {
        type: 'linear',
        reverse: true,
        title: { display: true, text: `TVD (${lengthUnit})` },
        grid: { color: '#e2e8f0' },
      },
    },
    plugins: { legend: { display: false } },
  };

  const chartH = trajType === 'custom' ? '182px' : '230px';

  const classBg = result ? {
    'Low': 'green.200', 'Medium': 'yellow.200', 'High': 'orange.200', 'Very High': 'red.200'
  }[result.classification] || 'gray.100' : 'gray.100';

  const classText = result ? {
    'Low': 'green.800', 'Medium': 'yellow.800', 'High': 'orange.800', 'Very High': 'red.800'
  }[result.classification] || 'gray.600' : 'gray.600';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalHeader><CalculatorIcon mr={2} boxSize={5} />DDI Trajectory Calculator</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Row 1: Units + Trajectory Type — compact bar */}
            <HStack spacing={6} align="center">
              <HStack spacing={1}>
                <Text fontSize="xs" fontWeight="semibold">Units:</Text>
                <Text fontSize="xs" fontWeight="bold" color="brand.500">{isMetric ? 'Metric' : 'Imperial'}</Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="xs" fontWeight="semibold">Trajectory:</Text>
                <RadioGroup value={trajType} onChange={setTrajType}>
                  <HStack spacing={3}>
                    <Radio value="jshape" size="sm">J Shape</Radio>
                    <Radio value="sshape" size="sm">S Shape</Radio>
                    <Radio value="custom" size="sm">Custom</Radio>
                  </HStack>
                </RadioGroup>
              </HStack>
            </HStack>

            {/* Row 2: Full-width inputs — fixed height */}
            <Box borderWidth="1px" borderRadius="md" p={3}>
              {trajType === 'jshape' && (
                <HStack spacing={2} align="flex-end" py={1}>
                  <NumInput label="KOP" value={jInputs.kop} onChange={(v) => updateJ('kop', v)} unit={lengthUnit} min={1} />
                  <NumInput label="Build rate" value={jInputs.build} onChange={(v) => updateJ('build', v)} unit={rateUnit} />
                  <NumInput label="Inclination" value={jInputs.inc} onChange={(v) => updateJ('inc', v)} unit="deg" min={1} max={90} />
                  <NumInput label="TD MD" value={jInputs.td} onChange={(v) => updateJ('td', v)} unit={lengthUnit} />
                </HStack>
              )}
              {trajType === 'sshape' && (
                <HStack spacing={2} align="flex-end" py={1}>
                  <NumInput label="KOP 1" value={sInputs.kop1} onChange={(v) => updateS('kop1', v)} unit={lengthUnit} min={1} />
                  <NumInput label="Build 1" value={sInputs.br1} onChange={(v) => updateS('br1', v)} unit={rateUnit} />
                  <NumInput label="Inc 1" value={sInputs.inc1} onChange={(v) => updateS('inc1', v)} unit="deg" min={1} max={90} />
                  <NumInput label="KOP 2" value={sInputs.kop2} onChange={(v) => updateS('kop2', v)} unit={lengthUnit} min={1} />
                  <NumInput label="Build/Drop 2" value={sInputs.br2} onChange={(v) => updateS('br2', v)} unit={rateUnit} />
                  <NumInput label="Inc 2" value={sInputs.inc2} onChange={(v) => updateS('inc2', v)} unit="deg" min={1} max={90} />
                  <NumInput label="TD MD" value={sInputs.td} onChange={(v) => updateS('td', v)} unit={lengthUnit} />
                </HStack>
              )}
              {trajType === 'custom' && (
                <Flex gap={4} align="stretch" h="100%">
                  <Box
                    ref={dropRef}
                    flex={1}
                    border="2px dashed"
                    borderColor="blue.400"
                    borderRadius="md"
                    textAlign="center"
                    bg="blue.50"
                    cursor="pointer"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <VStack spacing={1}>
                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                        {csvFileName || 'Drag & Drop CSV Here or Click to Browse'}
                      </Text>
                      <Text fontSize="2xs" color="gray.500">CSV with columns: MD (Measured Depth), INC (Inclination), AZ (Azimuth)</Text>
                    </VStack>
                  </Box>
                  <Box fontSize="2xs" fontFamily="mono" flexShrink={0} border="1px solid" borderColor="gray.300" borderRadius="sm" overflow="hidden" alignSelf="center">
                    <Box bg="gray.200" px={2} py={0.5} textAlign="center" borderBottom="1px solid" borderColor="gray.300">
                      <Text fontSize="2xs" fontWeight="semibold" color="gray.600">EXAMPLE CSV</Text>
                    </Box>
                    {[
                      { cells: ['MD', 'INC', 'AZ'], isHeader: true },
                      { cells: ['0', '0', '0'] },
                      { cells: ['500', '5', '10'] },
                      { cells: ['1000', '30', '10'] },
                    ].map((row, i) => (
                      <Flex key={i} borderBottom={i < 3 ? '1px solid' : 'none'} borderColor="gray.200">
                        {row.cells.map((cell, j) => (
                          <Box
                            key={j}
                            px={2}
                            py={0.5}
                            w="50px"
                            textAlign="right"
                            borderRight={j < 2 ? '1px solid' : 'none'}
                            borderColor="gray.200"
                            bg={row.isHeader ? 'gray.100' : 'white'}
                            color={row.isHeader ? 'gray.600' : 'gray.500'}
                            fontWeight={row.isHeader ? 'semibold' : 'normal'}
                          >
                            {cell}
                          </Box>
                        ))}
                      </Flex>
                    ))}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      handleCsvFile(e.target.files[0]);
                      e.target.value = null;
                    }}
                  />
                </Flex>
              )}
            </Box>

            {/* Error */}
            {error && (
              <Alert status="error" borderRadius="md" py={2}>
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}

            {/* Row 2: Chart + Results side by side */}
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <Box flex={2} borderWidth="1px" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="semibold" mb={1}>Trajectory Plot</Text>
                {chartData ? (
                  <Box h={chartH}>
                    <Line data={chartData} options={chartOptions} />
                  </Box>
                ) : (
                  <Flex h={chartH} align="center" justify="center">
                    <Text fontSize="sm" color="gray.400">Enter parameters to see trajectory</Text>
                  </Flex>
                )}
              </Box>

              <Box flex={1} borderWidth="1px" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="semibold" mb={2}>Results</Text>
                {result ? (
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="xs">Max. AHD:</Text>
                      <Text fontSize="xs" fontWeight="bold">{result.maxAHD} {lengthUnit}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs">Max. Tort:</Text>
                      <Text fontSize="xs" fontWeight="bold">{result.maxTort} {rateUnit}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs">Max. TVD:</Text>
                      <Text fontSize="xs" fontWeight="bold">{result.maxTVD} {lengthUnit}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm">DDI:</Text>
                      <Text fontSize="md" fontWeight="bold" color="blue.600">{result.maxDDI}</Text>
                    </HStack>
                    <Box bg={classBg} borderRadius="md" p={2} textAlign="center" mt={1}>
                      <Text fontSize="2xs" fontWeight="semibold" color={classText} textTransform="uppercase" letterSpacing="wide">Classification</Text>
                      <Text fontSize="lg" fontWeight="bold" color={classText}>{result.classification}</Text>
                    </Box>
                  </VStack>
                ) : (
                  <Flex h={chartH} align="center" justify="center">
                    <Text fontSize="sm" color="gray.400">No results yet</Text>
                  </Flex>
                )}
              </Box>
            </Flex>

            {/* Abbreviations — compact inline */}
            <Flex wrap="wrap" gap={2} px={1}>
              {ABBREVIATIONS.map(([abbr, full]) => (
                <Text key={abbr} fontSize="2xs" color="gray.400">
                  <Text as="span" fontWeight="semibold">{abbr}</Text>: {full}
                </Text>
              ))}
            </Flex>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSendResult} isDisabled={!result}>
            Apply DDI Result
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
